import * as React from "react";
import apiClient from "@/api-client";
import { Autocomplete, Chip, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { PlusCircleIcon } from "@phosphor-icons/react/dist/ssr/PlusCircle";
import { FormProvider, useForm } from "react-hook-form";
import { CaretDown } from "@phosphor-icons/react";

type Option = {
	label: string;
	value: string;
};

type Props = {
	label?: string;
	placeholder: string;
	value: Option[] | undefined;
	setValue: (value: Option[]) => void;
	withOptions?: boolean;
	getOptionsUrl?: string;
	fetchLabel?: string;
	fetchValue?: string;
	isTags?: boolean;
	defaultOptions?: Option[];
	maxValues?: number;
};

export function ChipsFreeSolo({
	label,
	placeholder,
	value,
	setValue,
	withOptions = false,
	getOptionsUrl,
	fetchLabel,
	fetchValue,
	isTags = false,
	defaultOptions,
	maxValues,
}: Props) {
	const [inputValue, setInputValue] = React.useState("");
	const inputRef = React.useRef<HTMLInputElement>(null);
	const [options, setOptions] = React.useState<Option[]>(defaultOptions || []);
	const [isOpen, setIsOpen] = React.useState(false);
	const [isMounted, setIsMounted] = React.useState(false);
	const [loading, setLoading] = React.useState(false);

	const fetchOptions = React.useCallback(async () => {
    if (defaultOptions) {
      setOptions(defaultOptions);
      return;
    }
		try {
			setIsMounted(true);
			setLoading(true);
			const response = await apiClient.get(getOptionsUrl || "");
			setOptions(
				response.data?.map((item: any) => ({ label: item[fetchLabel || "label"], value: item[fetchValue || "value"] }))
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [getOptionsUrl, fetchLabel, fetchValue, defaultOptions]);

	const handleOpen = () => {
		setIsOpen(true);

		if (!isMounted && withOptions) {
			fetchOptions();
		}
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	const methods = useForm({
		defaultValues: {
			metaKeywords: [],
		},
	});

	const handleAddValue = () => {
		if (inputValue.trim()) {
			const newOption = { label: inputValue.trim(), value: inputValue.trim() } as Option;
			const currentValues = value || [];
			const exists = currentValues.some((item) => item.value === newOption.value);

			if (!exists) {
				setValue([...currentValues, newOption]);
			}
			setInputValue("");
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
			handleAddValue();
		}
	};

	const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()));

	return (
		<Box sx={{ width: "100%", position: "relative" }}>
			<FormProvider {...methods}>
				<Autocomplete
					freeSolo
          popupIcon={<CaretDown size={18} />}   
					disableCloseOnSelect
					options={options}
					disableClearable
					multiple
					open={withOptions ? isOpen : false}
					onOpen={handleOpen}
					onClose={handleClose}
					loading={loading}
					inputValue={inputValue}
					onInputChange={(event, newInputValue) => {
						setInputValue(newInputValue);
					}}
					selectOnFocus
					clearOnBlur
					handleHomeEndKeys
					getOptionLabel={(option: Option | string) => (typeof option === "string" ? option : option.label)}
					onChange={(event, newValue) => {
            const formatted = newValue?.map((item) => (typeof item === "string" ? { label: item, value: item } : item));
						if (maxValues && newValue.length > maxValues) {
              setValue(formatted.slice(formatted.length - maxValues));
							return;
						}

						setValue(formatted);
					}}
					isOptionEqualToValue={(option: Option, v: Option) => option.value === v.value}
					value={value || []}
					renderInput={(params) => (
            <TextField
            {...params}
            placeholder={
              maxValues && (value?.length || 0) >= maxValues
                ? "Maximum reached"
                : value?.length === 0
                ? placeholder
                : ""
            }
            size="small"
            label={label}
            onKeyPress={handleKeyPress}
            inputRef={inputRef}
            InputProps={{
              ...params.InputProps,
              readOnly: !!(maxValues && (value?.length || 0) >= maxValues),
              endAdornment:
                inputValue &&
                (!maxValues || (value?.length || 0) < maxValues) ? (
                  <IconButton
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onClick={handleAddValue}
                    color="primary"
                  >
                    <PlusCircleIcon width={20} height={20} weight="fill" />
                  </IconButton>
                ) : null,
            }}
          />
					)}
					renderOption={(props, option) => (
						<li {...props}>
							{isTags ? (
								<Chip
									label={typeof option === "string" ? option : option.label}
									size="small"
									variant="filled"
									color="primary"
									sx={{ fontSize: "12px", height: "24px" }}
								/>
							) : typeof option === "string" ? (
								option
							) : (
								option.label
							)}
						</li>
					)}
					slotProps={{
						chip: { color: "primary", size: "small", sx: { margin: "0 !important", mr: "8px !important", my: '0px !important' } },
						popper: {
							sx: {
								display: filteredOptions.length === 0 && !loading ? "none" : "block",
								"& .MuiAutocomplete-option": {
									margin: "4px 0 !important",
									fontSize: "16px !important",
								},
							},
						},
						paper: {
							sx: {
								fontSize: "16px !important",
							},
						},
					}}
					sx={{
						width: "100%",
						"& .MuiInputBase-input": {
							cursor: "pointer",
							fontSize: "16px",
              paddingLeft: '6px !important',
              py: '0 !important',
						},
            "& .MuiInputBase-root": {
              minHeight: '40px !important',
              display: 'flex',
              gap: '2px !important',
            }
					}}
				/>
			</FormProvider>
		</Box>
	);
}
